import { Injectable, Injector, createNgModule } from "@angular/core";
import { BehaviorSubject, filter, from, map, tap } from "rxjs";
import { AppConfigService } from "@App/app-config.service";
import { LoaderConfig, LOADER_CONFIG_LIST } from "./module-loader-config";
import { ApplicationItemCodeEnum } from "@Portal/Core/Services/AccountService/ApplicationItemCodeEnum";

const INITIAL_STATE_MODULES_LOADED = new BehaviorSubject<boolean>(false);

@Injectable({ providedIn: "root" })
export class ModuleLoaderService {

    private loadedModules = new Map<string, Promise<void>>();

    constructor(
        private injector: Injector,
        private appConfigService: AppConfigService,
    ) {}

    loadStateModules() {

        return this.appConfigService.startupModelLoadingStatus$
            .pipe(
                filter(status => status === "LOADED"),
                tap(() => {

                    const configs = this.filterStateConfigs();

                    from(Promise.all(configs.map(config => this.loadAndActivateModule(config))))
                        .subscribe(() => { INITIAL_STATE_MODULES_LOADED.next(true); });
                }),
                map(() => true),
            );
    }

    waitForStateModules() {

        return INITIAL_STATE_MODULES_LOADED.pipe(filter(loaded => loaded));
    }

    private filterStateConfigs() {

        const installedApps = this.appConfigService.profile.availableApplicationItemList;
        const locationStates = this.appConfigService.profile.assignedLocationList.map(i => i.state);

        return LOADER_CONFIG_LIST.filter(config => {

            return installedApps.some(appCode => config.applicationList.indexOf(appCode) > -1)
                || locationStates.some(locationState => locationState === config.state);
        });
    }

    loadModuleByAppCode(appCode: ApplicationItemCodeEnum) {

        const config = LOADER_CONFIG_LIST.find(i => i.applicationList.includes(appCode));
        if (!config) throw new Error(`There is no loader config for app code ${appCode}`);

        return this.loadAndActivateModule(config);
    }

    // A state module is normally loaded by the `/retail` guard; solutions outside Retail that need a state's
    // handles have to ask for the one state they are about, and a missing config is a legitimate case there.
    loadModuleByState(state: string) {

        const config = LOADER_CONFIG_LIST.find(i => i.state === state?.toUpperCase());

        return config
            ? this.loadAndActivateModule(config)
            : Promise.resolve();
    }

    private loadAndActivateModule(config: LoaderConfig) {

        // Instantiating a module twice re-registers its handles and duplicates its subscriptions; a failed load is
        // forgotten instead, otherwise one chunk error would block the state for the rest of the session.
        const loading = this.loadedModules.get(config.state) ?? this.activateModule(config).catch(error => {

            this.loadedModules.delete(config.state);
            throw error;
        });

        this.loadedModules.set(config.state, loading);

        return loading;
    }

    private async activateModule(config: LoaderConfig) {

        const module = await config.loadModule();
        createNgModule(module, this.injector);
    }
}
